import { apiClient } from "@/lib/api/client";
import { buildQueryString } from "@/lib/utils/query-string";
import type { OrderListParams, OrderListResponse, Order, TodoItem, TodoListParams, DashboardStats } from "../types/order.types";
import type { OrderInvoiceContext, OrderQuoteContext } from "../types/printing.types";
import type { PaginatedResponse } from "@/lib/api/types";

const BASE = "/api/orders/";

// ─── Print documents (invoice / quote) ──────────────────────────────────────
// JSON data feeds the native pages; the *Pdf functions fetch the rendered PDF
// as a Blob (the Bearer header rides along, unlike a tab navigation — which is
// why the old server-rendered pages 404'd for the React app).

export async function getOrderInvoice(id: number): Promise<OrderInvoiceContext> {
  const { data } = await apiClient.get<OrderInvoiceContext>(`${BASE}${id}/invoice/`);
  return data;
}

export async function getOrderQuote(id: number, leadTime?: string): Promise<OrderQuoteContext> {
  const query = leadTime ? `?lead_time=${encodeURIComponent(leadTime)}` : "";
  const { data } = await apiClient.get<OrderQuoteContext>(`${BASE}${id}/quote/${query}`);
  return data;
}

export async function getOrderInvoicePdf(id: number): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}${id}/invoice/pdf/`, { responseType: "blob" });
  return data;
}

export async function getOrderQuotePdf(id: number, leadTime?: string): Promise<Blob> {
  const query = leadTime ? `?lead_time=${encodeURIComponent(leadTime)}` : "";
  const { data } = await apiClient.get(`${BASE}${id}/quote/pdf/${query}`, { responseType: "blob" });
  return data;
}

export async function listOrders(params: OrderListParams) {
  const query = buildQueryString(params as Record<string, unknown>);
  const { data } = await apiClient.get<OrderListResponse>(`${BASE}?${query}`);
  return data;
}

export async function getOrderDetails(id: number) {
  const { data } = await apiClient.get<Order>(`${BASE}${id}/`);
  return data;
}

export async function getOrderShort(id: number) {
  const { data } = await apiClient.get<Order>(`${BASE}${id}/short/`);
  return data;
}

export async function createOrder(order: Record<string, unknown>) {
  const { data } = await apiClient.post(BASE, order);
  return data;
}

export async function createProjectOrder(payload: {
  projectId: number;
  name: string;
  groups?: Record<string, unknown>[];
}): Promise<Order> {
  const { data } = await apiClient.post<Order>(BASE, {
    kind: "project",
    project_id: payload.projectId,
    name: payload.name,
    groups: payload.groups ?? [],
  });
  return data;
}

export async function patchOrder(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}${id}/`, payload);
  return data;
}

// Assign or move an order to a different project. The order adopts the
// target project's discount. There is no "unlink" — a project order
// always stays tied to a project. ``createInvoice`` gives a submitted
// customer order without an invoice a new order invoice.
export async function switchOrderProject(
  id: number,
  projectId: number,
  createInvoice = false,
): Promise<Order> {
  const { data } = await apiClient.post<Order>(`${BASE}${id}/switch_project/`, {
    project_id: projectId,
    create_invoice: createInvoice,
  });
  return data;
}

export async function importOrder(order: FormData) {
  const { data } = await apiClient.post(`${BASE}importorder/`, order);
  return data;
}

export async function submitOrder(id: number, formData: FormData) {
  const { data } = await apiClient.post(`${BASE}${id}/submit/`, formData);
  return data;
}

export async function exportOrder(id: number) {
  const { data } = await apiClient.get(`${BASE}${id}/export/`, { responseType: "blob" });
  return data;
}

export async function exportOrderAsYaml(id: number) {
  const { data } = await apiClient.get(`${BASE}${id}/yaml/`, { responseType: "blob" });
  return data;
}

export async function getOrderExportContent(id: number) {
  const { data } = await apiClient.get<string>(`${BASE}${id}/export_content/`, {
    responseType: "text",
    transformResponse: [(d: string) => d],
  });
  return data;
}

export async function getOrderYamlContent(id: number) {
  const { data } = await apiClient.get<string>(`${BASE}${id}/yaml_content/`, {
    responseType: "text",
    transformResponse: [(d: string) => d],
  });
  return data;
}

export async function approveOrder(id: number) {
  const { data } = await apiClient.post(`${BASE}${id}/approve/`);
  return data;
}

export async function declineOrder(id: number) {
  const { data } = await apiClient.post(`${BASE}${id}/decline/`);
  return data;
}

export async function recalculateOrder(id: number) {
  const { data } = await apiClient.post(`${BASE}${id}/recalculate/`);
  return data;
}

export async function getPaymentOptions(id: number, date?: string) {
  const query = date ? `?date=${date}` : "";
  const { data } = await apiClient.get(`${BASE}${id}/payment_options/${query}`);
  return data;
}

export async function fixOrder(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}${id}/fix/`, payload);
  return data;
}

export async function cloneOrder(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}${id}/clone/`, payload);
  return data;
}

export async function addGroup(orderId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}${orderId}/add_group/`, payload);
  return data;
}

export async function addOrderFiles(id: number, files: FormData) {
  const { data } = await apiClient.post(`${BASE}${id}/add_files/`, files);
  return data;
}

export async function getDymoXmls(id: number, mode: string) {
  const { data } = await apiClient.get(`${BASE}${id}/dymo_xmls/?mode=${mode}`);
  return data;
}

export async function applyDiscountCode(id: number, code: string) {
  const { data } = await apiClient.post(`${BASE}${id}/discount_code/`, { discount_code: code });
  return data;
}

export async function cancelDiscountCode(id: number) {
  const { data } = await apiClient.post(`${BASE}${id}/cancel_discount_code/`);
  return data;
}

export async function startEditing(id: number) {
  const { data } = await apiClient.post(`${BASE}${id}/start_editing/`);
  return data;
}

export async function stopEditing(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}${id}/stop_editing/`, payload);
  return data;
}

export async function getShelfList(id: number) {
  const { data } = await apiClient.get(`${BASE}${id}/shelf_list/`);
  return data;
}

export async function updateOrderCustomDiscount(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}${id}/update_custom_discount/`, payload);
  return data;
}

export async function updateBypassPaymentOptionDiscount(id: number, bypass: boolean) {
  const { data } = await apiClient.post(`${BASE}${id}/update_bypass_payment_option_discount/`, { bypass_payment_option_discount: bypass });
  return data;
}

export async function resolveOrderNote(orderId: number, noteId: number) {
  const { data } = await apiClient.post(`${BASE}${orderId}/resolve_order_note/`, { note_id: noteId });
  return data;
}

export async function updateOrderNote(orderId: number, noteId: number, note: string) {
  const { data } = await apiClient.patch(`${BASE}${orderId}/update_order_note/`, { note_id: noteId, note });
  return data;
}

export interface PrintListOrder {
  id: number;
  list_comment?: string;
}

export interface PrintListOrderRow {
  order_id: number;
  order_name: string;
  customer_name: string;
  status: string;
  status_display: string;
  job_priority: string;
  job_priority_display: string;
  nb_cabinets: number;
  requested_date: string | null;
  completion_date: string | null;
  partial_completed_timestamp: string | null;
  order_total: string;
  comment: string | null;
  is_late: boolean;
  invoice_total_price: string | null;
  sales_tax: string | null;
  payment_option_discount_amount: string | null;
  cc_processing_fees: string | null;
}

export interface PrintListTotals {
  count: number;
  nb_cabinets: number;
  order_total: string;
  total_price: string | null;
  sales_tax: string | null;
  payment_option_discount_amount: string | null;
}

export interface PrintListPayload {
  list_uuid: string;
  type: "order_list" | "revenue_report";
  title: string | null;
  timestamp: string;
  orders: PrintListOrderRow[];
  totals: PrintListTotals;
}

export async function createPrintList(
  orders: PrintListOrder[],
  title: string,
  list_type = "order_list",
) {
  const { data } = await apiClient.post(`${BASE}print_list/`, { orders, title, list_type });
  return data as { list_uuid: string; url: string };
}

export async function getPrintList(token: string) {
  const { data } = await apiClient.get(`${BASE}print_list/${token}/`);
  return data as PrintListPayload;
}

export async function downloadPrintListPDF(token: string): Promise<Blob> {
  const { data } = await apiClient.get(`${BASE}print_list/${token}/pdf/`, { responseType: "blob" });
  return data;
}

export async function requestItemRemake(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}remake/`, payload);
  return data;
}

export async function getRemakeOrders(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}remake_list/?${query}`);
  return data;
}

export async function getRemakeRequestList(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}remake/?${query}`);
  return data;
}

export async function approveRemakeRequest(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}remake/${id}/approve/`, payload);
  return data;
}

export async function rejectRemakeRequest(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}remake/${id}/reject/`, payload);
  return data;
}

export async function getScanSessions(id: number) {
  const { data } = await apiClient.get(`${BASE}${id}/scan_sessions/`);
  return data;
}

export async function getCompletedShippedLog(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}logs/completed_shipped_log/?${query}`);
  return data;
}

export async function printSession(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}print_session/`, payload);
  return data;
}

export async function addStatusDocs(id: number, formData: FormData) {
  const { data } = await apiClient.post(`${BASE}${id}/add_status_docs/`, formData);
  return data;
}

export async function addTodo(orderId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}${orderId}/todo/`, payload);
  return data;
}

export async function completeTodo(orderId: number, todoId: number) {
  const { data } = await apiClient.patch(`${BASE}${orderId}/todo/${todoId}/complete/`);
  return data;
}

export async function deleteTodo(orderId: number, todoId: number) {
  const { data } = await apiClient.delete(`${BASE}${orderId}/todo/${todoId}/delete/`);
  return data;
}

export async function getAllTodos(params: TodoListParams) {
  const query = buildQueryString(params as Record<string, unknown>);
  const { data } = await apiClient.get<PaginatedResponse<TodoItem>>(`${BASE}todos/?${query}`);
  return data;
}

export async function getTermsOfUse(id: number) {
  const { data } = await apiClient.get(`${BASE}${id}/terms_of_use/`);
  return data;
}

export async function getOrderComments(id: number) {
  const { data } = await apiClient.get(`${BASE}${id}/comments/`);
  return data;
}

export async function addOrderComment(id: number, text: string) {
  const { data } = await apiClient.post(`${BASE}${id}/comments/`, { text });
  return data;
}

const COMMENT_BASE = "/api/comments/";

export async function updateComment(commentId: number, text: string) {
  const { data } = await apiClient.put(`${COMMENT_BASE}${commentId}/updateComment/`, { text });
  return data;
}

export async function deleteComment(commentId: number) {
  const { data } = await apiClient.delete(`${COMMENT_BASE}${commentId}/deleteComment/`);
  return data;
}

export async function confirmComment(commentId: number) {
  const { data } = await apiClient.post(`${COMMENT_BASE}${commentId}/confirm/`);
  return data;
}

export async function ignoreComment(commentId: number) {
  const { data } = await apiClient.post(`${COMMENT_BASE}${commentId}/ignore/`);
  return data;
}

export async function resetComment(commentId: number) {
  const { data } = await apiClient.post(`${COMMENT_BASE}${commentId}/reset/`);
  return data;
}

export async function deferComment(commentId: number, notes: string) {
  const { data } = await apiClient.post(`${COMMENT_BASE}${commentId}/defer/`, { notes });
  return data;
}

export async function getCompletedOrdersReport() {
  const { data } = await apiClient.get(`${BASE}completed_report/`);
  return data;
}

export async function getCompletedOrdersChart(sampleRate: string) {
  const { data } = await apiClient.get(`${BASE}completed_chart/?sample_rate=${sampleRate}`);
  return data;
}

export async function getCreatedOrdersChart(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}created_chart/?${query}`);
  return data;
}

export async function getPartialCompletedOrdersReport() {
  const { data } = await apiClient.get(`${BASE}partial_completed_report/`);
  return data;
}

export async function getLateReport(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}late_report/?${query}`);
  return data;
}

export async function getLateOrdersChart(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}late_orders_chart/?${query}`);
  return data;
}

export async function getDashboardStats() {
  const { data } = await apiClient.get<DashboardStats>("/api/orders/dashboardstats/");
  return data;
}
